<template>
    <div class="tournament-prize-pool" v-if="shouldShowPrizePool">
        <!-- Desktop Version -->
        <div class="d-none d-sm-block">
            <TournamentPrizePoolCard
                v-if="prizePoolData"
                :prize-pool="prizePoolData"
                :participant-count="participantCount"
                @invite="openShareModal" />
        </div>

        <!-- Mobile Version -->
        <div class="d-block d-sm-none">
            <TournamentPrizePoolMobile
                v-if="prizePoolData"
                :prize-pool="prizePoolData"
                :participant-count="participantCount"
                @invite="openShareModal" />
        </div>

        <!-- Share Modal -->
        <v-dialog v-model="showShareModal" max-width="500px">
            <v-card>
                <v-card-title>
                    <span>{{ $t('inviteParticipants') || 'Invite Participants' }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="showShareModal = false">
                        <v-icon>fa-regular fa-times</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <div class="link-section">
                        <p class="mb-4">{{ $t('rankingLinkDescription') || 'Share this link to invite participants to join the tournament' }}</p>
                        <v-text-field
                            v-model="shareableLink"
                            readonly
                            outlined
                            :label="$t('shareableLink') || 'Shareable Link'"
                            append-icon="fa-regular fa-copy"
                            @click:append="copyToClipboard">
                        </v-text-field>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import TournamentPrizePoolCard from './TournamentPrizePoolCard.vue';
import TournamentPrizePoolMobile from './TournamentPrizePoolMobile.vue';
import TournamentRepository from '../../../repositories/tournament';

export default {
    name: 'TournamentPrizePool',
    components: {
        TournamentPrizePoolCard,
        TournamentPrizePoolMobile
    },
    data() {
        return {
            prizePoolData: null,
            loading: false,
            error: null,
            refreshInterval: null,
            showShareModal: false
        };
    },
    computed: {
        participantCount() {
            return this.$store.state.tournament?.participants?.length || 0;
        },
        shouldShowPrizePool() {
            // Only show if we have valid prize pool data
            return this.prizePoolData !== null;
        },
        shareableLink() {
            const tournament = this.$store.state.tournament;
            if (!tournament) return '';

            // Get externalId from tournamentSettings
            const externalId = tournament.tournamentSettings?.externalId;

            if (!externalId) {
                return '';
            }

            return `https://app.swiset.com/tournaments/embed-rank?tournamentId=${externalId}`;
        }
    },
    watch: {
        '$store.state.tournament': {
            immediate: true,
            handler(tournament) {
                if (tournament && tournament.tournamentId) {
                    this.fetchPrizePool();
                    this.setupAutoRefresh();
                }
            }
        }
    },
    beforeDestroy() {
        this.clearAutoRefresh();
    },
    methods: {
        async fetchPrizePool() {
            if (!this.$store.state.tournament?.tournamentId) return;

            this.loading = true;
            try {
                const response = await TournamentRepository.getTournamentPrizePool(
                    this.$store.state.tournament.tournamentId
                );

                if (response.status === 200) {
                    this.prizePoolData = response.data;
                    this.error = null;
                }
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    // No prize pool exists - component won't show
                    this.prizePoolData = null;
                } else {
                    this.error = error;
                    console.error('Error fetching prize pool:', error);
                }
            } finally {
                this.loading = false;
            }
        },
        setupAutoRefresh() {
            // Clear any existing interval
            this.clearAutoRefresh();

            // Set up auto-refresh every 30 seconds
            this.refreshInterval = setInterval(() => {
                // Only refresh if document is visible (not in background tab)
                if (!document.hidden) {
                    this.fetchPrizePool();
                }
            }, 30000); // 30 seconds
        },
        clearAutoRefresh() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = null;
            }
        },
        openShareModal() {
            this.showShareModal = true;
        },
        copyToClipboard() {
            navigator.clipboard.writeText(this.shareableLink).then(() => {
                // Successfully copied - could emit an event or show feedback if needed
            }).catch(err => {
                //eslint-disable-next-line
                console.error('Could not copy text: ', err);
            });
        }
    }
};
</script>

<style scoped lang="scss">
.tournament-prize-pool {
    width: 100%;
}
</style>
